import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { ArrowLeft, BookOpen, Calendar, User, Search, TrendingUp } from 'lucide-react';

const BlogPage = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');

  const blogPosts = [
    {
      id: 1,
      title: "Die 10 häufigsten Fehler beim Deutschlernen",
      excerpt: "Vermeide diese typischen Anfängerfehler und lerne effektiver Deutsch. Von Artikeln bis zu Verben - hier sind die wichtigsten Stolpersteine.",
      author: "Dr. Maria Schmidt",
      date: "15. November 2024",
      readTime: "5 Min",
      category: "Lerntipps",
      image: "https://via.placeholder.com/400x200/4ECDC4/FFFFFF?text=Deutsch+Lernen"
    },
    {
      id: 2,
      title: "Warum Stories beim Sprachenlernen so effektiv sind",
      excerpt: "Wissenschaftliche Studien zeigen: Geschichten helfen beim Vokabellernen. Erfahre, warum unser Gehirn Narratives so gut verarbeitet.",
      author: "Prof. Thomas Müller",
      date: "12. November 2024",
      readTime: "8 Min",
      category: "Wissenschaft",
      image: "https://via.placeholder.com/400x200/45B7B8/FFFFFF?text=Stories+Lernen"
    },
    {
      id: 3,
      title: "A1 bis C2: Welches Level habe ich wirklich?",
      excerpt: "Der ultimative Guide zum Gemeinsamen Europäischen Referenzrahmen. Teste dein Niveau und finde das richtige Lernmaterial.",
      author: "Anna Weber",
      date: "10. November 2024",
      readTime: "12 Min",
      category: "Level-Guide",
      image: "https://via.placeholder.com/400x200/FF6B6B/FFFFFF?text=A1-C2+Level"
    },
    {
      id: 4,
      title: "Deutsche Grammatik einfach erklärt: Der Akkusativ",
      excerpt: "Wen oder was? Die häufigste Frage beim Deutschlernen. Hier lernst du den Akkusativ mit einfachen Beispielen und Merkhilfen.",
      author: "Michael Fischer",
      date: "8. November 2024",
      readTime: "6 Min",
      category: "Grammatik",
      image: "https://via.placeholder.com/400x200/96CEB4/FFFFFF?text=Akkusativ"
    },
    {
      id: 5,
      title: "Erfolgsstory: Von A1 auf B2 in 6 Monaten",
      excerpt: "Lisa erzählt, wie sie mit DMV-Lernportal in nur einem halben Jahr fließend Deutsch gelernt hat. Ihre besten Tipps und Strategien.",
      author: "Lisa Johnson",
      date: "5. November 2024",
      readTime: "7 Min",
      category: "Erfolgsgeschichte",
      image: "https://via.placeholder.com/400x200/FECA57/FFFFFF?text=Erfolg"
    },
    {
      id: 6,
      title: "Die besten Apps zum Deutschlernen 2024",
      excerpt: "Ein ehrlicher Vergleich der Top Sprachlern-Apps. Was können sie wirklich und welche ist die richtige für dich?",
      author: "Tech Team",
      date: "3. November 2024",
      readTime: "15 Min",
      category: "App-Vergleich",
      image: "https://via.placeholder.com/400x200/54A0FF/FFFFFF?text=Apps+2024"
    }
  ];

  const categories = ["Alle", "Lerntipps", "Grammatik", "Wissenschaft", "Erfolgsgeschichte"];
  const [selectedCategory, setSelectedCategory] = useState("Alle");

  const filteredPosts = blogPosts.filter(post => {
    const matchesCategory = selectedCategory === "Alle" || post.category === selectedCategory;
    const matchesSearch = post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         post.excerpt.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-white to-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <Button variant="ghost" onClick={() => navigate('/')} className="mr-4">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Zurück
              </Button>
              <BookOpen className="h-8 w-8 text-[#4ECDC4] mr-2" />
              <span className="text-2xl font-bold text-slate-900">DMV-Lernportal Blog</span>
            </div>
            <Button onClick={() => navigate('/auth')} className="bg-[#4ECDC4] hover:bg-[#45b7aa]">
              Anmelden
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-slate-900 mb-4">
            Deutschlernen leicht gemacht
          </h1>
          <p className="text-xl text-slate-600 mb-8">
            Tipps, Tricks und wissenschaftlich fundierte Methoden für deinen Lernerfolg
          </p>
          
          {/* Search Bar */}
          <div className="max-w-md mx-auto relative mb-8">
            <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Blog durchsuchen..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>

          {/* Category Filter */}
          <div className="flex flex-wrap justify-center gap-2">
            {categories.map((category) => (
              <Button
                key={category}
                variant={selectedCategory === category ? "default" : "outline"}
                onClick={() => setSelectedCategory(category)}
                className={selectedCategory === category ? "bg-[#4ECDC4] hover:bg-[#45b7aa]" : ""}
              >
                {category}
              </Button>
            ))}
          </div>
        </div>

        {/* Blog Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            <div className="space-y-8">
              {filteredPosts.map((post) => (
                <Card key={post.id} className="hover:shadow-xl transition-all cursor-pointer group">
                  <div className="aspect-video bg-gradient-to-r from-[#4ECDC4] to-[#45b7aa] rounded-t-lg"></div>
                  <CardHeader>
                    <div className="flex items-center justify-between mb-2">
                      <Badge variant="secondary">{post.category}</Badge>
                      <div className="flex items-center text-sm text-slate-500">
                        <Calendar className="h-4 w-4 mr-1" />
                        {post.date}
                      </div>
                    </div>
                    <CardTitle className="text-xl group-hover:text-[#4ECDC4] transition-colors">
                      {post.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-slate-600 mb-4">{post.excerpt}</p>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center text-sm text-slate-500">
                        <User className="h-4 w-4 mr-1" />
                        {post.author}
                      </div>
                      <span className="text-sm text-slate-500">{post.readTime} Lesezeit</span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Load More */}
            <div className="text-center mt-12">
              <Button variant="outline" className="px-8">
                Weitere Artikel laden
              </Button>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Newsletter */}
            <Card className="bg-gradient-to-br from-[#4ECDC4] to-[#45b7aa] text-white">
              <CardHeader>
                <CardTitle>📧 Newsletter abonnieren</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="mb-4 text-white/90">
                  Erhalte wöchentlich die besten Lerntipps direkt in dein Postfach.
                </p>
                <div className="space-y-3">
                  <Input 
                    placeholder="Deine E-Mail Adresse" 
                    className="bg-white text-slate-900"
                  />
                  <Button variant="secondary" className="w-full">
                    Anmelden
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Popular Posts */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <TrendingUp className="h-5 w-5 mr-2" />
                  Beliebteste Artikel
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {blogPosts.slice(0, 3).map((post, index) => (
                  <div key={post.id} className="flex items-start space-x-3 cursor-pointer hover:bg-slate-50 p-2 rounded">
                    <div className="text-xl font-bold text-[#4ECDC4] min-w-[2rem]">
                      {index + 1}
                    </div>
                    <div>
                      <h4 className="font-medium text-slate-900 text-sm leading-tight mb-1">
                        {post.title}
                      </h4>
                      <p className="text-xs text-slate-500">{post.readTime} • {post.date}</p>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Tags */}
            <Card>
              <CardHeader>
                <CardTitle>🏷️ Beliebte Themen</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {['Artikel', 'Präpositionen', 'Verben', 'A1-Level', 'B1-Level', 'Aussprache', 'Wortschatz', 'Prüfung'].map((tag) => (
                    <Badge key={tag} variant="outline" className="cursor-pointer hover:bg-[#4ECDC4] hover:text-white">
                      {tag}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* App Promotion */}
            <Card className="bg-slate-100">
              <CardContent className="p-6 text-center">
                <BookOpen className="h-12 w-12 text-[#4ECDC4] mx-auto mb-4" />
                <h3 className="font-bold text-slate-900 mb-2">DMV-Lernportal App</h3>
                <p className="text-sm text-slate-600 mb-4">
                  Lerne unterwegs mit unserer App. Über 1000 Stories warten auf dich!
                </p>
                <Button onClick={() => navigate('/auth')} className="w-full bg-[#4ECDC4] hover:bg-[#45b7aa]">
                  App testen
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlogPage;